import React, {useState} from 'react';
import {Link} from 'react-router';
import {useTracking} from 'react-tracking';

import type {Placement} from '@popperjs/core';
import {IconButton, Menu} from '@albert-io/atomic';

import {
  COPYRIGHT_TEXT,
  CopyrightModalContent
} from 'client/components/CopyrightModal/CopyrightModal.react';
import {IssuesModal} from 'client/Modals/IssuesModal/IssuesModal';
import QuestionTrackerModal from 'client/components/PracticeView/QuestionTrackerModal/QuestionTrackerModal.react';

interface Props {
  questionId: string;
  /** Whether to include the "Question feedback" item (requires a valid session). */
  showFeedback?: boolean;
  /**
   * When both are provided, includes a "My usage" item that opens the question
   * tracker modal. Supplied by the fused FRQ grading footer.
   */
  questionSetId?: string;
  userId?: string;
  /** When set, includes an "Edit question" item linking to the Dennis editor (super only). */
  editQuestionPath?: string;
  /** Where the menu (and the trigger's tooltip) opens. Defaults to above the trigger, for a footer. */
  position?: Placement;
}

/**
 * Single overflow menu for a question's actions, opened from an `ellipsis` ("•••")
 * trigger: in the grading view's question header, and in the practice footer.
 *
 * Consolidates every footer affordance into one menu — question feedback, the
 * teacher's "My usage" view, and the copyright affordance (which stands in for
 * the hidden page-level `<Footer minimal />`) — so the toolbar needs only a
 * single icon instead of separate "info" and "more" buttons.
 */
export default function QuestionActionsMenu({
  questionId,
  showFeedback = true,
  questionSetId,
  userId,
  editQuestionPath,
  position = 'top-start'
}: Props) {
  const {trackEvent} = useTracking();
  const [isCopyrightModalOpen, setIsCopyrightModalOpen] = useState(false);
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [isUsageModalOpen, setIsUsageModalOpen] = useState(false);

  const showUsage = Boolean(questionSetId && userId);
  const tooltipPlacement = position.startsWith('bottom') ? 'bottom' : 'top';

  return (
    <>
      <Menu position={position} positionFixed closeOnItemClick>
        <Menu.Trigger>
          {(triggerProps) => (
            <IconButton
              icon={['far', 'ellipsis']}
              label='More actions'
              tooltip='More actions'
              // Off while the menu is open, so it doesn't sit on top of the menu.
              tooltipProps={{placement: tooltipPlacement, enabled: !triggerProps['aria-expanded']}}
              data-testid='practice-view__question-actions-menu-btn'
              {...triggerProps}
            />
          )}
        </Menu.Trigger>
        {/* z-20! lifts the popover above the grading panel's sticky header
            (z-10); ! is needed to beat the unlayered .o-menu-content__wrapper
            z-index:1 from the shared menu SCSS. */}
        <Menu.Content className='z-20!'>
          {showUsage && (
            <Menu.Item
              leftIconProps={{icon: ['far', 'question']}}
              onClick={() => {
                trackEvent({content: 'My usage', event: 'click', type: 'button'});
                setIsUsageModalOpen(true);
              }}
            >
              My Usage
            </Menu.Item>
          )}
          {showFeedback && (
            <Menu.Item
              leftIconProps={{icon: ['far', 'message-question']}}
              onClick={() => setIsFeedbackModalOpen(true)}
            >
              Question Feedback
            </Menu.Item>
          )}
          {editQuestionPath && (
            <Menu.Item
              as={Link}
              to={editQuestionPath}
              className='no-underline'
              leftIconProps={{icon: ['far', 'pencil']}}
            >
              Edit Question
            </Menu.Item>
          )}
          {/* Matches the copyright item in the adaptive practice view dropdown. */}
          <Menu.Item
            textProps={{size: 'xs', color: 'tertiary', className: 'text-nowrap text-center'}}
            onClick={() => setIsCopyrightModalOpen(true)}
            className='items-center'
          >
            {COPYRIGHT_TEXT}
          </Menu.Item>
        </Menu.Content>
      </Menu>
      {isCopyrightModalOpen && (
        <CopyrightModalContent onClose={() => setIsCopyrightModalOpen(false)} />
      )}
      {isFeedbackModalOpen && (
        <IssuesModal questionId={questionId} handleClose={() => setIsFeedbackModalOpen(false)} />
      )}
      {isUsageModalOpen && questionSetId && userId && (
        <QuestionTrackerModal
          questionSetId={questionSetId}
          userId={userId}
          handleClose={() => setIsUsageModalOpen(false)}
        />
      )}
    </>
  );
}
